import React, { useState, useEffect, useRef } from "react";
import { formatLastRun, getNextRunTime, formatSchedule } from "../automationUtils";
import { cfThumbnail } from "../../../../utils/imageUtils";
import { FaListUl } from "react-icons/fa6";
import { AUTOMATION_TYPES, TYPE_STRING_TO_UI_KEY, isCatalogueTaskType } from "../config/automationTypes";

// Inject keyframes for delete overlay animation
const deleteOverlayStyles = `
@keyframes deleteOverlayIn {
  0% {
    opacity: 0;
    transform: scale(0.96);
    filter: blur(4px);
  }
  100% {
    opacity: 1;
    transform: scale(1);
    filter: blur(0px);
  }
}
`;

// Add styles to document head (only once)
if (typeof document !== 'undefined') {
  const styleId = 'timeline-listitem-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = deleteOverlayStyles;
    document.head.appendChild(style);
  }
}

// ============================================
// Live indicator dot with ping animation
// ============================================
export const LiveDot = ({ color = "amber" }) => {
  const colors = {
    amber: { ping: "bg-amber-400", dot: "bg-amber-500" },
    emerald: { ping: "bg-emerald-400", dot: "bg-emerald-500" },
  };
  const c = colors[color] || colors.amber;
  return (
    <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
      <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${c.ping} opacity-75`} />
      <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${c.dot}`} />
    </span>
  );
};

// ============================================
// LeftColumn - Automation info card with hover overlay
// ============================================
const LeftColumn = ({ automation, isSelected, onToggleSelect, onSettings, onDelete, onViewRunDetails }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const latestRun = automation.timeline?.[0];
  const hasRunningTask = automation.timeline?.some(
    (run) => run.status === 'running' || run.status === 'queued'
  );
  const isQueued = latestRun?.status === 'queued';
  const isRunning = latestRun?.status === 'running';
  const isActive = isQueued || isRunning;
  const extractionCount = latestRun?.extraction?.rowCount || 0;
  const finalCount = latestRun?.itemCount || extractionCount;
  const journalStep = latestRun?.last_journal_step;

  // Tasks that process a list of user-supplied root URLs. For these we show
  // root-page progress ("X of Y URLs") instead of a generic row count — sub-pages
  // crawled internally are tracked separately and not counted here.
  const isUrlProgressType =
    automation.type === 'extract_page_details' ||
    automation.type === 'extract_pages' ||
    automation.type === 'extract_emails' ||
    automation.type === 'extract_social_links';

  const isContactExtractor =
    automation.type === 'extract_emails' || automation.type === 'extract_social_links';
  const isSocialExtractor = automation.type === 'extract_social_links';

  // Render the appropriate status display based on automation state
  const renderStatus = () => {
    // Live email/social extractor: once a contact is found, show the count
    // (emerald, State 1 style); until then mirror the page extractor's running
    // view — "Extracting — A of B URLs" with an amber Running dot.
    if (isRunning && isContactExtractor) {
      const scan = latestRun?.subPageScan;
      const count = isSocialExtractor ? (scan?.socialFound || 0) : (scan?.emailsFound || 0);
      const label = isSocialExtractor ? 'socials extracted' : 'emails extracted';
      const stats = latestRun?.stats;
      const done = (stats?.completed || 0) + (stats?.failed || 0) + (stats?.blocked || 0);

      if (count > 0) {
        const statusDetail = stats?.total
          ? <span className="text-emerald-400/70 dark:text-emerald-500/70"> · {done} of {stats.total} URLs</span>
          : null;
        return (
          <div className="space-y-1">
            <div className="flex items-baseline gap-2">
              <span className="text-2xl lg:text-3xl font-light text-emerald-600 dark:text-emerald-400 tabular-nums tracking-tight">
                {count}
              </span>
              <span className="text-xs lg:text-sm text-gray-500 dark:text-gray-500">{label}</span>
            </div>
            <p className="text-xs text-emerald-500 dark:text-emerald-400 leading-tight flex items-center gap-1.5">
              <LiveDot color="emerald" />
              Extracting{statusDetail || ''}
            </p>
          </div>
        );
      }

      return (
        <div className="space-y-1.5">
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-snug line-clamp-2">
            {stats?.total ? `Extracting — ${done} of ${stats.total} URLs` : 'Starting up'}
          </p>
          <p className="text-xs text-amber-500 dark:text-amber-400 leading-tight flex items-center gap-1.5">
            <LiveDot color="amber" />
            Running
          </p>
        </div>
      );
    }

    // State 1: Running with extraction in progress - show live row count
    if (isRunning && extractionCount > 0) {
      const pageCount = journalStep?.metadata?.['Page Count'] || journalStep?.metadata?.Page;
      const stats = latestRun?.stats;
      const catalogue = latestRun?.catalogue;
      const isCatalogueTask = isCatalogueTaskType(automation.type);

      // Build type-specific status detail
      let statusLabel = 'Extracting';
      let statusDetail = null;
      if (isCatalogueTask && (catalogue?.card_status || catalogue?.phase_label)) {
        statusLabel = catalogue.card_status || catalogue.phase_label;
      } else if (isUrlProgressType && stats?.total) {
        const done = (stats.completed || 0) + (stats.failed || 0) + (stats.blocked || 0);
        statusDetail = <span className="text-emerald-400/70 dark:text-emerald-500/70"> · {done} of {stats.total} URLs</span>;
      } else if (pageCount) {
        statusDetail = <span className="text-emerald-400/70 dark:text-emerald-500/70"> · page {pageCount}</span>;
      }

      return (
        <div className="space-y-1">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl lg:text-3xl font-light text-emerald-600 dark:text-emerald-400 tabular-nums tracking-tight">
              {extractionCount}
            </span>
            <span className="text-xs lg:text-sm text-gray-500 dark:text-gray-500">rows extracted</span>
          </div>
          <p className="text-xs text-emerald-500 dark:text-emerald-400 leading-tight flex items-center gap-1.5">
            <LiveDot color="emerald" />
            {statusLabel}{statusDetail || ''}
          </p>
        </div>
      );
    }

    // State 2: Active (running/queued) with journal step - show current step
    if (isActive && journalStep) {
      const stats = latestRun?.stats;
      let description = journalStep.description;

      // Enrich with root-URL progress for list-of-URLs task types
      if (isRunning && isUrlProgressType && stats?.total) {
        const done = (stats.completed || 0) + (stats.failed || 0) + (stats.blocked || 0);
        description = `Extracting \u2014 ${done} of ${stats.total} URLs`;
      }

      return (
        <div className="space-y-1.5">
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-snug line-clamp-2">
            {description}
          </p>
          <p className="text-xs text-amber-500 dark:text-amber-400 leading-tight flex items-center gap-1.5">
            <LiveDot color="amber" />
            {isQueued ? 'In queue' : 'Running'}
          </p>
        </div>
      );
    }

    // State 3: Active but no journal yet - waiting or just starting
    if (isActive) {
      return (
        <div className="space-y-1.5">
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-snug">
            {isQueued ? 'Waiting in queue' : 'Starting up'}
          </p>
          <p className="text-xs text-amber-500 dark:text-amber-400 leading-tight flex items-center gap-1.5">
            <LiveDot color="amber" />
            {isQueued ? 'Queued' : 'Initializing'}
          </p>
        </div>
      );
    }

    const formatLastRunLine = () => {
      const runDate = new Date(latestRun.timestamp);
      const formattedDate = `${runDate.getDate()} ${runDate.toLocaleDateString('en-GB', { month: 'short' })}`;
      const formattedTime = runDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
      return `Last run: ${formattedDate} at ${formattedTime}`;
    };

    // State 4a: Completed list-of-URLs task - show root-page progress (X of Y URLs)
    const completedStats = latestRun?.stats;
    if (isUrlProgressType && completedStats?.total) {
      const done = (completedStats.completed || 0) + (completedStats.failed || 0) + (completedStats.blocked || 0);
      return (
        <div className="space-y-1">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl lg:text-3xl font-light text-gray-900 dark:text-gray-100 tabular-nums tracking-tight">
              {done}
            </span>
            <span className="text-xs lg:text-sm text-gray-500 dark:text-gray-500">of {completedStats.total} URLs</span>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 leading-tight">
            {formatLastRunLine()}
          </p>
        </div>
      );
    }

    // State 4b: Completed with results - show final count
    if (finalCount > 0) {
      return (
        <div className="space-y-1">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl lg:text-3xl font-light text-gray-900 dark:text-gray-100 tabular-nums tracking-tight">
              {finalCount}
            </span>
            <span className="text-xs lg:text-sm text-gray-500 dark:text-gray-500">rows extracted</span>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 leading-tight">
            {formatLastRunLine()}
          </p>
        </div>
      );
    }

    // State 5: No data or terminal status with 0 rows
    const statusMessage = !latestRun
      ? 'No extractions yet'
      : latestRun.status === 'failed'
        ? 'Last run failed'
        : latestRun.status === 'stopped'
          ? (latestRun.stoppedReason === 'insufficient_credits' ? 'Ran out of credits' : 'Stopped')
          : 'No rows found';

    return (
      <p className="text-xs lg:text-sm text-gray-400 dark:text-gray-500">
        {statusMessage}
      </p>
    );
  };

  return (
    <div className="bg-white dark:bg-white/[0.02] border border-gray-200/60 dark:border-white/[0.06] rounded-2xl p-2.5 lg:p-3 h-full relative group/card transition-all duration-300 shadow-sm hover:shadow-card-hover dark:hover:shadow-card-hover-dark hover:border-gray-300/60 dark:hover:border-white/[0.1] overflow-hidden">
      {/* Delete confirmation overlay */}
      {showDeleteConfirm && (
        <div
          className={`absolute inset-0 z-30 rounded-2xl flex flex-col overflow-hidden transition-all duration-300 ease-out
            ${isExiting ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
          style={{
            animation: isExiting ? 'none' : 'deleteOverlayIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)'
          }}
        >
          {/* Frosted glass background */}
          <div className="absolute inset-0 bg-white/90 dark:bg-gray-900/95 backdrop-blur-xl" />

          {/* Content container */}
          <div className="relative z-10 h-full flex flex-col justify-between p-2.5 lg:p-3">
            {/* Top section: Icon + Title + Description */}
            <div className="flex items-start gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-100 to-rose-100 dark:from-red-950/60 dark:to-rose-950/40 flex items-center justify-center flex-shrink-0">
                <svg className="w-3.5 h-3.5 text-red-500 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight">Delete automation?</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2 leading-snug">
                  <span className="font-medium text-gray-600 dark:text-gray-300">{automation.name}</span>
                </p>
              </div>
            </div>

            {/* Actions - always at bottom */}
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={() => {
                  setIsExiting(true);
                  setTimeout(() => {
                    setShowDeleteConfirm(false);
                    setIsExiting(false);
                  }, 200);
                }}
                disabled={isDeleting}
                className="flex-1 px-3 py-2 text-xs font-medium text-gray-600 dark:text-gray-400
                  bg-white/60 dark:bg-gray-800/60 hover:bg-white dark:hover:bg-gray-800
                  border border-gray-200/80 dark:border-gray-700/50
                  rounded-lg transition-all duration-200
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setIsDeleting(true);
                  try {
                    await onDelete(automation.id);
                    setIsExiting(true);
                    setTimeout(() => {
                      setShowDeleteConfirm(false);
                      setIsExiting(false);
                      setIsDeleting(false);
                    }, 200);
                  } catch (e) {
                    setIsDeleting(false);
                  }
                }}
                disabled={isDeleting}
                className="flex-1 px-3 py-2 text-xs font-medium text-white
                  bg-gradient-to-r from-red-500 to-rose-500
                  hover:from-red-600 hover:to-rose-600
                  rounded-lg transition-all duration-200 shadow-sm
                  disabled:opacity-70 disabled:cursor-not-allowed
                  flex items-center justify-center gap-1.5"
              >
                {isDeleting ? (
                  <>
                    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Deleting</span>
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Accent line - red when delete confirm visible, blue on hover */}
      <div className={`absolute left-0 top-0 bottom-0 rounded-l-2xl transition-all duration-300
        ${showDeleteConfirm
          ? 'w-1 opacity-100 bg-gradient-to-b from-red-400 to-rose-500 shadow-[2px_0_8px_rgba(239,68,68,0.3)] z-40'
          : 'w-0.5 opacity-0 group-hover/card:opacity-100 group-hover/card:w-1 bg-gradient-to-b from-blue-400 via-cyan-400 to-purple-400 shadow-[2px_0_12px_rgba(34,211,238,0.2)]'
        }`} />

      {/* Top-right utility actions */}
      <div className={`absolute top-2 right-2 z-20 flex items-center gap-0.5 transition-opacity duration-200 ${isSelected ? 'opacity-100' : 'opacity-0 group-hover/card:opacity-100'}`}>
        {/* Open website - compact */}
        {automation.website && (
          <a
            href={automation.website}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors"
            title="Open website"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        )}

        {/* Select checkbox - compact */}
        {onToggleSelect && (
          <button
            type="button"
            role="checkbox"
            aria-checked={isSelected}
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelect(automation.id);
            }}
            className={`p-1.5 rounded-lg transition-colors ${
              isSelected
                ? 'text-blue-500'
                : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/50'
            }`}
            title={isSelected ? 'Deselect' : 'Select'}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
              {isSelected ? (
                <>
                  <rect x="4" y="4" width="16" height="16" rx="4" className="fill-blue-500" />
                  <path d="M9 12l2 2 4-4" stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                </>
              ) : (
                <rect x="4" y="4" width="16" height="16" rx="4" className="stroke-current" strokeWidth={1.5} />
              )}
            </svg>
          </button>
        )}
      </div>

      {/* Type tile - tinted badge per type; fades on hover to reveal bottom-right actions */}
      <div className="absolute bottom-2.5 right-2.5 z-10 opacity-100 group-hover/card:opacity-0 transition-opacity duration-200 pointer-events-none">
        {(() => {
          const typeConfig = AUTOMATION_TYPES[TYPE_STRING_TO_UI_KEY[automation.type]];
          const TypeIcon = typeConfig?.icon || FaListUl;
          const cc = typeConfig?.colorClasses;
          return (
            <div className={`w-6 h-6 rounded-full flex items-center justify-center opacity-80 ${cc?.iconTileBg || "bg-gray-400/20"}`}>
              <TypeIcon className={`w-3 h-3 ${cc?.text ? cc.text : "text-gray-400 dark:text-gray-500"}`} />
            </div>
          );
        })()}
      </div>

      {/* Bottom-right action buttons */}
      <div className="absolute bottom-2 right-2 z-20 opacity-0 group-hover/card:opacity-100 transition-opacity duration-200 flex items-center gap-1">
        {/* Delete button */}
        {onDelete && (
          <div className="relative group/delete">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!hasRunningTask) setShowDeleteConfirm(true);
              }}
              disabled={hasRunningTask}
              className={`p-2.5 rounded-xl transition-colors ${
                hasRunningTask
                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                  : 'text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
            {/* Custom tooltip */}
            <div className="absolute bottom-full right-0 mb-2 opacity-0 group-hover/delete:opacity-100 transition-opacity duration-200 pointer-events-none">
              <div className="bg-gray-900 dark:bg-gray-800 text-white text-xs font-medium px-2.5 py-1.5 rounded-lg whitespace-nowrap shadow-lg">
                {hasRunningTask ? 'Cannot delete while running' : 'Delete automation'}
                <div className="absolute top-full right-3 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45 -translate-y-1" />
              </div>
            </div>
          </div>
        )}

        {/* Edit button */}
        {onSettings && (
          <div className="relative group/edit">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSettings(automation);
              }}
              className="p-2.5 rounded-xl text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
            {/* Custom tooltip */}
            <div className="absolute bottom-full right-0 mb-2 opacity-0 group-hover/edit:opacity-100 transition-opacity duration-200 pointer-events-none">
              <div className="bg-gray-900 dark:bg-gray-800 text-white text-xs font-medium px-2.5 py-1.5 rounded-lg whitespace-nowrap shadow-lg">
                Edit automation
                <div className="absolute top-full right-3 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45 -translate-y-1" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Card Content */}
      <div className="h-full flex flex-col justify-between relative z-10">
        {/* Top section: Name and URL */}
        <div className="mb-auto">
          <div className="flex items-start gap-3 min-w-0">
            {automation.favicon && (
              <img
                src={automation.favicon}
                alt=""
                className="w-5 h-5 rounded-md flex-shrink-0 mt-0.5 opacity-80"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            )}
            <div className="min-w-0 flex-1">
              <h3 className={`text-sm font-medium text-gray-900 dark:text-gray-100 truncate leading-snug transition-[padding] duration-200 ${isSelected ? 'pr-16' : 'group-hover/card:pr-16'}`}>{automation.name}</h3>
              <p className="text-xs text-gray-500 dark:text-gray-500 truncate mt-1">{automation.website}</p>
            </div>
          </div>
        </div>

        {/* Bottom section: Status - clickable when there's a run */}
        <div
          className={`pt-3 lg:pt-4 ${latestRun ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
          onClick={() => latestRun && onViewRunDetails?.(automation)}
        >
          {renderStatus()}
        </div>
      </div>
    </div>
  );
};

// ============================================
// CenterColumn - Timeline chart with actions
// ============================================
const CenterColumn = ({
  automation,
  timeline,
  timeRange,
  scheduledAutomations,
  onTimelineClick,
  onViewData,
  onViewHistory,
  onViewRunDetails,
  isRunning,
  isStopping,
  onRunNow,
  onStop,
  onSchedule,
  onResume,
  scheduleButtonRef,
}) => {
  const [hoveredTimestamp, setHoveredTimestamp] = useState(null);
  const [isStopCooldown, setIsStopCooldown] = useState(false);
  const wasStoppingRef = useRef(false);

  // Track when isStopping transitions from true to false (stop completed)
  useEffect(() => {
    if (wasStoppingRef.current && !isStopping) {
      setIsStopCooldown(true);
      const timer = setTimeout(() => setIsStopCooldown(false), 5000);
      return () => clearTimeout(timer);
    }
    wasStoppingRef.current = isStopping;
  }, [isStopping]);

  // Determine if automation has running/queued tasks (from backend data or parent's optimistic state)
  const hasRunningTask = automation.timeline?.some(
    (run) => run.status === 'running' || run.status === 'queued'
  );

  let timeWindow, slotDuration, timeFormat;

  // Standardize to ~96 bars for consistent visual appearance on wide screens
  const targetBars = 96;

  switch (timeRange) {
    case "hourly":
      timeWindow = 60 * 60 * 1000;
      slotDuration = timeWindow / targetBars;
      timeFormat = "minute";
      break;
    case "weekly":
      timeWindow = 7 * 24 * 60 * 60 * 1000;
      slotDuration = timeWindow / targetBars;
      timeFormat = "day";
      break;
    case "monthly":
      timeWindow = 30 * 24 * 60 * 60 * 1000;
      slotDuration = timeWindow / targetBars;
      timeFormat = "week";
      break;
    case "raw":
      if (timeline.length > 0) {
        const oldestRun = Math.min(...timeline.map(t => t.timestamp));
        timeWindow = Date.now() - oldestRun;
      } else {
        timeWindow = 24 * 60 * 60 * 1000;
      }
      slotDuration = timeWindow / targetBars;
      timeFormat = "auto";
      break;
    case "daily":
    default:
      timeWindow = 24 * 60 * 60 * 1000;
      slotDuration = timeWindow / targetBars;
      timeFormat = "hour";
      break;
  }

  const now = Date.now();
  const maxBars = targetBars;

  const groupedRuns = [];
  for (let i = 0; i < maxBars; i++) {
    const slotStart = now - timeWindow + i * slotDuration;
    const slotEnd = slotStart + slotDuration;

    const runsInSlot = timeline.filter((run) => run.timestamp >= slotStart && run.timestamp < slotEnd);

    if (runsInSlot.length > 0) {
      const sortedRuns = [...runsInSlot].sort((a, b) => b.timestamp - a.timestamp);
      const mostRecentRun = sortedRuns[0];

      const hasRunning = sortedRuns.some((r) => r.status === "running");
      const hasQueued = sortedRuns.some((r) => r.status === "queued");

      let displayStatus = mostRecentRun.status;
      if (hasRunning) {
        displayStatus = "running";
      } else if (hasQueued) {
        displayStatus = "queued";
      }

      groupedRuns.push({
        ...mostRecentRun,
        status: displayStatus,
        count: runsInSlot.length,
        runs: sortedRuns,
        slotStart,
        slotEnd,
      });
    } else {
      groupedRuns.push({
        timestamp: slotStart,
        status: "empty",
        count: 0,
        slotStart,
        slotEnd,
      });
    }
  }

  const maxHeight = 38;
  const barHeight = () => maxHeight * 0.85;

  const getTimeLabel = (timestamp) => {
    const date = new Date(timestamp);
    switch (timeFormat) {
      case "minute":
        return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      case "hour":
        return date.toLocaleTimeString([], { hour: "numeric" });
      case "day":
        return date.toLocaleDateString([], { weekday: "short" });
      case "week":
        return date.toLocaleDateString([], { month: "short", day: "numeric" });
      case "auto":
        if (timeWindow < 2 * 60 * 60 * 1000) {
          return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        } else if (timeWindow < 48 * 60 * 60 * 1000) {
          return date.toLocaleTimeString([], { hour: "numeric" });
        } else if (timeWindow < 14 * 24 * 60 * 60 * 1000) {
          return date.toLocaleDateString([], { weekday: "short" });
        } else {
          return date.toLocaleDateString([], { month: "short", day: "numeric" });
        }
      default:
        return "";
    }
  };

  // Format time for tooltip (compact)
  const formatTooltipTime = (timestamp) => {
    return new Date(timestamp).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  // Format number with commas
  const formatNumber = (num) => num.toLocaleString();

  // Status colors and symbols
  const statusConfig = {
    success: { color: "text-emerald-500 dark:text-emerald-400", symbol: "✓" },
    failed: { color: "text-rose-500 dark:text-rose-400", symbol: "✗" },
    running: { color: "text-amber-500 dark:text-amber-400", symbol: "⟳" },
    queued: { color: "text-orange-500 dark:text-orange-400", symbol: "◷" },
    stopped: { color: "text-gray-400 dark:text-gray-500", symbol: "◼" },
    out_of_credits: { color: "text-amber-500 dark:text-amber-400", symbol: "⚠" },
  };

  const getDetailedTooltip = (point) => {
    const rowCount = point.itemCount || point.extraction?.rowCount || 0;
    const timeClass = "text-gray-400 dark:text-gray-500 text-[11px]";

    // Empty slot
    if (point.status === "empty") {
      const startTime = formatTooltipTime(point.slotStart);
      const endTime = new Date(point.slotEnd).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
      return (
        <div className="space-y-1">
          <div className="text-gray-500 dark:text-gray-400">No runs</div>
          <div className={timeClass}>{startTime} – {endTime}</div>
        </div>
      );
    }

    const time = formatTooltipTime(point.timestamp);
    const effectiveStatus = (point.status === 'stopped' && point.stoppedReason === 'insufficient_credits')
      ? 'out_of_credits' : point.status;
    const config = statusConfig[effectiveStatus] || statusConfig.stopped;

    // Multiple runs in slot
    if (point.count > 1) {
      const counts = { success: 0, failed: 0, running: 0, queued: 0, stopped: 0 };
      point.runs?.forEach((run) => {
        if (counts[run.status] !== undefined) counts[run.status]++;
      });

      const startTime = formatTooltipTime(point.slotStart);
      const endTime = new Date(point.slotEnd).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

      return (
        <div className="space-y-1.5">
          {/* Headline: row count or run count */}
          {rowCount > 0 ? (
            <div className="text-base font-medium">{formatNumber(rowCount)} rows</div>
          ) : (
            <div className="font-medium">{point.count} runs</div>
          )}
          {/* Status breakdown */}
          <div className="flex items-center gap-2 text-[11px]">
            {counts.success > 0 && <span className={statusConfig.success.color}>{statusConfig.success.symbol} {counts.success}</span>}
            {counts.failed > 0 && <span className={statusConfig.failed.color}>{statusConfig.failed.symbol} {counts.failed}</span>}
            {counts.running > 0 && <span className={statusConfig.running.color}>{statusConfig.running.symbol} {counts.running}</span>}
            {counts.queued > 0 && <span className={statusConfig.queued.color}>{statusConfig.queued.symbol} {counts.queued}</span>}
            {counts.stopped > 0 && <span className={statusConfig.stopped.color}>{statusConfig.stopped.symbol} {counts.stopped}</span>}
          </div>
          {/* Time range */}
          <div className={timeClass}>{startTime} – {endTime}</div>
        </div>
      );
    }

    // Single run - outcome first
    switch (effectiveStatus) {
      case "success":
        return (
          <div className="space-y-1">
            <div className={`font-medium ${config.color}`}>
              {rowCount > 0 ? <>{config.symbol} {formatNumber(rowCount)} rows</> : <>{config.symbol} Success</>}
            </div>
            <div className={timeClass}>{time}</div>
          </div>
        );

      case "failed":
        return (
          <div className="space-y-1">
            <div className={`font-medium ${config.color}`}>{config.symbol} Failed</div>
            {point.message && <div className="text-rose-400/80 dark:text-rose-300/80 text-[11px]">{point.message}</div>}
            {rowCount > 0 && <div className="text-gray-500 dark:text-gray-400">{formatNumber(rowCount)} rows before failure</div>}
            <div className={timeClass}>{time}</div>
          </div>
        );

      case "running":
        return (
          <div className="space-y-1">
            <div className={`font-medium ${config.color}`}>
              {config.symbol} {rowCount > 0 ? <>{formatNumber(rowCount)} rows</> : "Running"}
            </div>
            {point.last_journal_step?.description && (
              <div className="text-gray-500 dark:text-gray-400 truncate max-w-[200px] text-[11px]">{point.last_journal_step.description}</div>
            )}
            <div className={timeClass}>Started {time}</div>
          </div>
        );

      case "queued":
        return (
          <div className="space-y-1">
            <div className={`font-medium ${config.color}`}>{config.symbol} Waiting to start</div>
            <div className={timeClass}>Queued {time}</div>
          </div>
        );

      case "out_of_credits":
        return (
          <div className="space-y-1">
            <div className={`font-medium ${config.color}`}>
              {config.symbol} {rowCount > 0 ? <>{formatNumber(rowCount)} rows</> : "Out of credits"}
            </div>
            <div className="text-amber-400/80 dark:text-amber-300/80 text-[11px]">Ran out of credits</div>
            <div className={timeClass}>{time}</div>
          </div>
        );

      case "stopped":
        return (
          <div className="space-y-1">
            <div className={`font-medium ${config.color}`}>
              {config.symbol} {rowCount > 0 ? <>{formatNumber(rowCount)} rows</> : "Stopped"}
            </div>
            <div className={timeClass}>{time}</div>
          </div>
        );

      default:
        return (
          <div className="space-y-1">
            <div>{point.message || "Unknown"}</div>
            <div className={timeClass}>{time}</div>
          </div>
        );
    }
  };

  const timeMarkers = [];
  const markerCount = 5;
  for (let i = 0; i <= markerCount; i++) {
    const markerTime = now - timeWindow + (timeWindow / markerCount) * i;
    timeMarkers.push({
      position: (i / markerCount) * 100,
      label: getTimeLabel(markerTime),
      time: markerTime,
    });
  }

  const isEffectivelyRunning = isRunning || hasRunningTask;
  const canStop = hasRunningTask && !isStopping;

  // Resume button state
  const latestRun = automation.timeline?.[0];
  const latestRunStatus = latestRun?.status;
  const isTerminal = ['failed', 'stopped', 'cancelled'].includes(latestRunStatus);
  const showResume = isTerminal && !isEffectivelyRunning;
  // A checkpoint whose results have moved to long-term storage cannot be resumed — the run
  // would have nowhere to write. Say which of the two reasons applies, so the button being
  // greyed out is self-explanatory.
  const checkpointArchived = latestRun?.checkpointArchived;
  const isResumable = isTerminal && latestRun?.hasCheckpoint && !checkpointArchived;
  const resumeTooltip = isResumable
    ? 'Resume from last checkpoint'
    : checkpointArchived
      ? 'Resume not available — these results have moved to long-term storage. Start a new run.'
      : 'Resume not available — no checkpoint was saved';

  const handlePrimaryAction = () => {
    if (canStop) {
      onStop(automation);
    } else if (!isEffectivelyRunning && !isStopping) {
      onRunNow(automation.id);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-visible relative group/timeline">
      {/* Minimal header with primary actions + hover-revealed secondary actions */}
      <div className="flex items-center justify-between mb-2 flex-shrink-0">
        <div className="flex items-center gap-3">
          {/* Primary Run/Stop Button - Always Visible */}
          <div className="relative group/run">
            <button
              onClick={handlePrimaryAction}
              disabled={isStopping || isStopCooldown || (isEffectivelyRunning && !canStop)}
              className={`px-3 py-1.5 rounded-lg flex items-center gap-2
                transition-all duration-200 font-medium text-xs
                ${isStopping
                  ? 'bg-red-50 dark:bg-red-950/20 text-red-600 dark:text-red-400 cursor-not-allowed'
                  : canStop
                  ? 'bg-red-50 dark:bg-red-950/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-950/30'
                  : isStopCooldown
                  ? 'bg-gray-50 dark:bg-gray-800/50 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : isEffectivelyRunning
                  ? 'bg-amber-50 dark:bg-amber-950/20 text-amber-600 dark:text-amber-400 cursor-not-allowed'
                  : 'bg-emerald-50 dark:bg-emerald-950/20 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-950/30'
                }`}
            >
            {isStopping ? (
              <>
                <svg className="w-3.5 h-3.5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Stopping...</span>
              </>
            ) : canStop ? (
              <>
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <rect x="6" y="6" width="12" height="12" rx="2" />
                </svg>
                <span>Stop</span>
              </>
            ) : isEffectivelyRunning ? (
              <>
                <svg className="w-3.5 h-3.5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Running</span>
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
                <span>Run</span>
              </>
            )}
            </button>
            {/* Custom tooltip */}
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 opacity-0 group-hover/run:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="bg-gray-900 dark:bg-gray-800 text-white text-xs font-medium px-2.5 py-1.5 rounded-lg whitespace-nowrap shadow-lg">
                {isStopping ? 'Stopping the automation...' : canStop ? 'Stop the running automation' : isStopCooldown ? 'Please wait before running again' : isEffectivelyRunning ? 'Automation is currently running' : 'Start the automation now'}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45 translate-y-1" />
              </div>
            </div>
          </div>

          {/* Primary Schedule Button - Always Visible */}
          <div className="relative group/schedule">
            <button
              ref={scheduleButtonRef}
              onClick={() => onSchedule(automation)}
              className={`px-3 py-1.5 rounded-lg flex items-center gap-2
                transition-all duration-200 font-medium text-xs
                ${scheduledAutomations.has(automation.id) && automation.schedule
                  ? 'bg-blue-50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-950/30'
                  : 'bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>{scheduledAutomations.has(automation.id) && automation.schedule ? formatSchedule(automation.schedule) : 'Schedule'}</span>
              {scheduledAutomations.has(automation.id) && automation.schedule && (
                <svg className="w-3 h-3 text-emerald-500 dark:text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
            </button>
            {/* Custom tooltip */}
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 opacity-0 group-hover/schedule:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="bg-gray-900 dark:bg-gray-800 text-white text-xs font-medium px-2.5 py-1.5 rounded-lg whitespace-nowrap shadow-lg">
                {scheduledAutomations.has(automation.id) && automation.schedule
                  ? `Edit schedule · Next run ${getNextRunTime(automation.schedule)}`
                  : 'Set up automatic recurring runs'}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45 translate-y-1" />
              </div>
            </div>
          </div>

          {/* Primary Data Button - Always Visible */}
          {(() => {
            const latestRun = automation.timeline?.[0];
            const isActiveRun = latestRun?.status === 'running' || latestRun?.status === 'queued';
            const rowCount = latestRun?.extraction?.rowCount || latestRun?.itemCount || 0;
            const isDataDisabled = !automation.latestTableId || (isActiveRun && rowCount === 0);
            return (
              <div className="relative group/data">
                <button
                  onClick={() => onViewData(automation)}
                  disabled={isDataDisabled}
                  className={`px-3 py-1.5 rounded-lg flex items-center gap-2
                    transition-all duration-200 font-medium text-xs
                    ${!isDataDisabled
                      ? 'bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                      : 'bg-gray-50 dark:bg-gray-800/50 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                    }`}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                  </svg>
                  <span>Data</span>
                </button>
                {/* Custom tooltip */}
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 opacity-0 group-hover/data:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                  <div className="bg-gray-900 dark:bg-gray-800 text-white text-xs font-medium px-2.5 py-1.5 rounded-lg whitespace-nowrap shadow-lg">
                    {!isDataDisabled ? 'View extracted data in table' : 'No data extracted yet'}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45 translate-y-1" />
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Secondary Actions - Visible when running, otherwise on hover */}
          <div className={`flex items-center gap-1.5 transition-opacity duration-200 ${
            isEffectivelyRunning ? 'opacity-100' : 'opacity-0 group-hover/timeline:opacity-100'
          }`}>
            <div className="relative group/details">
              <button
                onClick={() => onViewRunDetails(automation)}
                disabled={!automation.timeline?.[0]}
                className={`p-1.5 rounded-lg transition-all duration-200 ${
                  !automation.timeline?.[0]
                    ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                    : isEffectivelyRunning
                    ? 'text-amber-600/70 dark:text-amber-500/70 hover:text-amber-700 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/20'
                    : 'text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/50'
                }`}
              >
                <svg className={`w-4 h-4 ${isEffectivelyRunning ? 'animate-[pulse_3s_ease-in-out_infinite]' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
              {/* Custom tooltip */}
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 opacity-0 group-hover/details:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                <div className="bg-gray-900 dark:bg-gray-800 text-white text-xs font-medium px-2.5 py-1.5 rounded-lg whitespace-nowrap shadow-lg">
                  {!automation.timeline?.[0] ? 'No runs yet' : isEffectivelyRunning ? 'View live run progress' : 'View last run details and logs'}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45 translate-y-1" />
                </div>
              </div>
            </div>

            <div className="relative group/history">
              <button
                onClick={() => onViewHistory(automation)}
                className="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-all duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
              {/* Custom tooltip */}
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 opacity-0 group-hover/history:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                <div className="bg-gray-900 dark:bg-gray-800 text-white text-xs font-medium px-2.5 py-1.5 rounded-lg whitespace-nowrap shadow-lg">
                  View all past runs
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45 translate-y-1" />
                </div>
              </div>
            </div>

            {/* Resume button - shown when latest run is failed/stopped/cancelled */}
            {showResume && (
              <div className="relative group/resume">
                <button
                  onClick={() => isResumable && onResume?.(automation, latestRun)}
                  disabled={!isResumable}
                  className={`p-1.5 rounded-lg transition-all duration-200 ${
                    isResumable
                      ? 'text-emerald-500 dark:text-emerald-400 hover:text-emerald-600 dark:hover:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/20'
                      : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
                {/* Custom tooltip */}
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 opacity-0 group-hover/resume:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                  <div className="bg-gray-900 dark:bg-gray-800 text-white text-xs font-medium px-2.5 py-1.5 rounded-lg whitespace-nowrap shadow-lg">
                    {resumeTooltip}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45 translate-y-1" />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Last Run Time - Right Side */}
        <span className="text-xs text-gray-400 dark:text-gray-500">
          {hoveredTimestamp ? formatLastRun(hoveredTimestamp) : formatLastRun(automation.lastRun)}
        </span>
      </div>

      {/* Timeline visualization */}
      <div className="flex-1 flex flex-col h-full">
        <div className="flex-1 relative overflow-visible">
          {/* Timeline bars */}
          <div className="relative flex items-end h-full gap-[2px]">
            {groupedRuns.map((point, index) => {
              const height = barHeight();
              const baseColor =
                point.status === "success"
                  ? "bg-gradient-to-t from-emerald-500 to-emerald-400"
                  : point.status === "failed"
                  ? "bg-gradient-to-t from-rose-500 to-rose-400"
                  : point.status === "running"
                  ? "bg-gradient-to-t from-amber-500 to-amber-400"
                  : point.status === "queued"
                  ? "bg-gradient-to-t from-orange-500 to-orange-400"
                  : point.status === "stopped"
                  ? "bg-gradient-to-t from-gray-500 to-gray-400"
                  : "bg-gray-200/40 dark:bg-gray-700/20";

              return (
                <div key={index} className="relative group flex-1 min-w-0">
                  <div
                    className={`${baseColor} transition-all duration-200 ${
                      point.status !== "empty" ? "cursor-pointer hover:brightness-110 hover:scale-y-110" : "cursor-default"
                    } relative`}
                    style={{
                      width: '100%',
                      height: `${height}px`,
                      borderRadius: '4px 4px 1px 1px',
                    }}
                    onMouseEnter={() => point.timestamp && setHoveredTimestamp(point.timestamp)}
                    onMouseLeave={() => setHoveredTimestamp(null)}
                    onClick={() => onTimelineClick(automation, point)}
                  >
                    {point.count > 1 && (
                      <span
                        className="absolute -top-4 left-1/2 transform -translate-x-1/2 text-[10px]
                                   text-gray-700 dark:text-gray-300 bg-white/95 dark:bg-gray-900/95 px-1.5 py-0.5 rounded opacity-0
                                   group-hover:opacity-100 font-medium transition-opacity duration-200 shadow-md"
                      >
                        {point.count}
                      </span>
                    )}
                  </div>

                  {/* Elegant tooltip */}
                  <div
                    className="absolute left-1/2 transform -translate-x-1/2 top-full mt-3 opacity-0
                                group-hover:opacity-100 transition-all duration-200 pointer-events-none z-[100]"
                  >
                    <div
                      className="bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 text-xs rounded-xl px-4 py-3 shadow-2xl
                                  border border-gray-200 dark:border-gray-800 min-w-[180px] leading-relaxed"
                    >
                      {getDetailedTooltip(point)}
                      <div
                        className="absolute -top-1.5 left-1/2 transform -translate-x-1/2
                                    w-3 h-3 bg-white dark:bg-gray-900 rotate-45 border-l border-t border-gray-200 dark:border-gray-800"
                      ></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Time markers - minimal styling */}
        <div className="flex justify-between mt-2 flex-shrink-0">
          {timeMarkers.map((marker, idx) => (
            <div key={idx} className="text-xs text-gray-400 dark:text-gray-600 text-center" style={{ width: `${100 / timeMarkers.length}%` }}>
              {marker.label}
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

// ============================================
// RightColumn - Screenshot preview
// ============================================
const RightColumn = ({ automation, onExpandImage }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const imgRef = useRef(null);
  const latestRun = automation.timeline?.[0];
  const isActive = latestRun?.status === 'running' || latestRun?.status === 'queued';

  // Get screenshots from automation or latest run
  const screenshots = automation.screenshots || latestRun?.screenshotUrls || [];
  const lastScreenshot = screenshots.length > 0 ? screenshots[screenshots.length - 1] : null;

  // Reset loaded state when screenshot changes
  useEffect(() => {
    setImageLoaded(false);
  }, [lastScreenshot]);

  // Check if image is already cached/complete after render
  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete && img.naturalHeight > 0) {
      setImageLoaded(true);
    }
  }, [lastScreenshot]);

  // State 1: Active run with no screenshots yet - show capturing state
  if (isActive && !lastScreenshot) {
    const isCatalogueTask = isCatalogueTaskType(automation.type);
    const catalogue = latestRun?.catalogue;
    const journalStep = latestRun?.last_journal_step;
    if (isCatalogueTask) {
      const listed = catalogue?.products_listed;
      const estimated = catalogue?.products_estimated;
      return (
        <div className="bg-white dark:bg-white/[0.02] border border-gray-200/60 dark:border-white/[0.06] rounded-2xl p-3 lg:p-4 h-full shadow-sm flex items-center justify-center">
          <div className="text-center px-1">
            <p className="text-xs text-gray-700 dark:text-gray-300 leading-snug line-clamp-2">
              {catalogue?.card_status || journalStep?.description || 'Starting catalogue extract'}
            </p>
            {listed != null && (
              <p className="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums mt-1">
                {Number(listed).toLocaleString()}{estimated ? ` / ~${Number(estimated).toLocaleString()}` : ''} listed
              </p>
            )}
            <p className="text-xs text-amber-500 dark:text-amber-400 flex items-center justify-center gap-1.5 mt-2">
              <LiveDot color="amber" />
              {catalogue?.phase_label || 'Extracting'}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white dark:bg-white/[0.02] border border-gray-200/60 dark:border-white/[0.06] rounded-2xl p-3 lg:p-4 h-full shadow-sm flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center mb-2">
            <svg className="w-5 h-5 lg:w-6 lg:h-6 text-amber-500 dark:text-amber-400 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <p className="text-xs text-amber-500 dark:text-amber-400 flex items-center justify-center gap-1.5">
            <LiveDot color="amber" />
            Capturing
          </p>
        </div>
      </div>
    );
  }

  // State 2: No screenshots and not active - show empty state
  if (!lastScreenshot) {
    return (
      <div className="bg-white dark:bg-white/[0.02] border border-gray-200/60 dark:border-white/[0.06] rounded-2xl p-3 lg:p-4 h-full shadow-sm flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-gray-100 dark:bg-gray-800/40 flex items-center justify-center mb-2">
            <svg className="w-5 h-5 lg:w-6 lg:h-6 text-gray-400 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500">No preview</p>
        </div>
      </div>
    );
  }

  // State 3: Has screenshot - show with loading skeleton
  return (
    <button
      onClick={() => onExpandImage?.({ screenshots, title: automation.name })}
      className="bg-white dark:bg-white/[0.02] border border-gray-200/60 dark:border-white/[0.06] rounded-2xl p-2 h-full shadow-sm hover:shadow-card-hover dark:hover:shadow-card-hover-dark hover:border-gray-300/60 dark:hover:border-white/[0.1] transition-all duration-300 group overflow-hidden w-full"
      title={`View ${screenshots.length} screenshot${screenshots.length > 1 ? 's' : ''}`}
    >
      <div className="relative h-full rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800/40">
        {/* Loading skeleton */}
        {!imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-pulse flex flex-col items-center gap-2">
              <svg className="w-6 h-6 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        )}

        <img
          ref={imgRef}
          key={lastScreenshot}
          src={cfThumbnail(lastScreenshot)}
          alt="Automation preview"
          loading="lazy"
          className={`w-full h-full object-cover transition-all duration-300 group-hover:scale-105 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
        />

        {/* Live indicator for active runs */}
        {isActive && imageLoaded && (
          <div className="absolute top-2 right-2">
            <span className="flex items-center gap-1 px-1.5 py-0.5 bg-black/60 backdrop-blur-sm rounded-full">
              <LiveDot color="amber" />
              <span className="text-[10px] text-white font-medium">Live</span>
            </span>
          </div>
        )}

        {/* Hover overlay with screenshot count */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
          {screenshots.length > 1 && (
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/95 dark:bg-gray-900/95 rounded-lg px-3 py-2 shadow-lg">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {screenshots.length} screenshots
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Click to view all</p>
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

// ============================================
// TimelineListitem - Main wrapper component
// ============================================
const TimelineListitem = ({
  automation,
  timeRange,
  scheduledAutomations,
  isRunning,
  isStopping,
  onTimelineClick,
  onViewData,
  onViewHistory,
  onViewRunDetails,
  onRunNow,
  onStop,
  onResume,
  onSettings,
  onSchedule,
  onDelete,
  onExpandImage,
  isSelected,
  onToggleSelect,
  scheduleButtonRef,
}) => {
  return (
    <div className="flex gap-2 lg:gap-2.5 xl:gap-3 relative min-h-[100px] lg:min-h-[115px]">
      {/* Left: Automation Info Card */}
      <div className="w-[220px] lg:w-[240px] xl:w-[260px] flex-shrink-0">
        <LeftColumn
          automation={automation}
          isSelected={isSelected}
          onToggleSelect={onToggleSelect}
          onSettings={onSettings}
          onDelete={onDelete}
          onViewRunDetails={onViewRunDetails}
        />
      </div>

      {/* Center: Timeline Chart with integrated action buttons */}
      <div className="bg-white dark:bg-white/[0.02] border border-gray-200/60 dark:border-white/[0.06] rounded-2xl p-2.5 lg:p-3 flex-1 min-w-0 shadow-sm hover:shadow-card-hover dark:hover:shadow-card-hover-dark hover:border-gray-300/60 dark:hover:border-white/[0.1] transition-all duration-300">
        <CenterColumn
          automation={automation}
          timeline={automation.timeline}
          timeRange={timeRange}
          scheduledAutomations={scheduledAutomations}
          onTimelineClick={onTimelineClick}
          onViewData={onViewData}
          onViewHistory={onViewHistory}
          onViewRunDetails={onViewRunDetails}
          isRunning={isRunning}
          isStopping={isStopping}
          onRunNow={onRunNow}
          onStop={onStop}
          onResume={onResume}
          onSchedule={onSchedule}
          scheduleButtonRef={scheduleButtonRef}
        />
      </div>

      {/* Right: Image Preview - Hidden on lg, visible on xl+ */}
      <div className="hidden md:block w-[160px] 2xl:w-[180px] flex-shrink-0">
        <RightColumn automation={automation} onExpandImage={onExpandImage} />
      </div>
    </div>
  );
};

// ============================================
// Exports
// ============================================
export default TimelineListitem;
